import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Bell, BellOff, ShieldAlert, EyeOff, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getConversation,
    getMessages,
    markConversationRead,
    sendMessage,
    isMuted,
    toggleMute,
    isPeerOnline,
    detectUnsafeContent,
    bumpViolations,
    getViolations,
} from "@/lib/socialStore";
import { listServicesByOwner } from "@/lib/entitiesStore";
import { getUsers } from "@/lib/storage";
import { isMessagingSuspended } from "@/lib/moderationStore";
import { isUserBlocked } from "@/lib/monetizationStore";
import { useSocialVersion, timeOfDay } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";

const VIOLATION_BLUR_THRESHOLD = 3;

const Chat = () => {
    const navigate = useNavigate();
    const { convId } = useParams();
    const { user } = useAuth();
    const v = useSocialVersion();
    const [draft, setDraft] = useState("");
    const [warning, setWarning] = useState(null);
    const scrollerRef = useRef(null);

    const conv = useMemo(() => (user ? getConversation(user.id, convId) : null), [user, convId, v]);
    const messages = useMemo(
        () => (user ? getMessages(user.id)[convId] || [] : []),
        [user, convId, v]
    );
    const muted = useMemo(
        () => (user ? isMuted(user.id, "direct", convId) : false),
        [user, convId, v]
    );
    const violations = useMemo(
        () => (user && conv ? getViolations(user.id, conv.peerId) : 0),
        [user, conv, v]
    );

    // Peer's business services — must be computed unconditionally to satisfy React hook rules.
    const peerUser = useMemo(
        () => (conv ? getUsers().find((u) => u.id === conv.peerId) || null : null),
        [conv]
    );
    const peerServices = useMemo(
        () => (conv ? listServicesByOwner(conv.peerId) || [] : []),
        [conv]
    );

    useEffect(() => {
        if (!user || !conv) return;
        sessionStorage.setItem("neara.openConv", convId);
        markConversationRead(user.id, convId);
        return () => sessionStorage.removeItem("neara.openConv");
    }, [user, conv, convId]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages.length]);

    if (!conv) {
        return (
            <div className="screen items-center justify-center" data-testid="chat-not-found">
                <p className="text-[14px] text-muted-foreground">Conversation not found.</p>
                <button onClick={() => navigate("/messages")} className="mt-3 text-primary text-[13px] font-medium">
                    Back to Messages
                </button>
            </div>
        );
    }

    const onSend = () => {
        const txt = draft.trim();
        if (!txt) return;
        if (isMessagingSuspended(user)) {
            setWarning("Messaging is suspended on your account. Contact support for help.");
            setTimeout(() => setWarning(null), 4500);
            return;
        }
        if (conv?.peerId && isUserBlocked(user.id, conv.peerId)) {
            setWarning("You blocked this user. Unblock them to start messaging again.");
            setTimeout(() => setWarning(null), 4500);
            return;
        }
        const unsafe = detectUnsafeContent(txt);
        if (unsafe) {
            bumpViolations(user.id, conv.peerId);
            setWarning(
                unsafe === "phone"
                    ? "Keep communication within the app for safety — phone numbers are flagged."
                    : "Keep communication within the app for safety — external messaging links are flagged."
            );
            // Auto-dismiss warning after 4s
            setTimeout(() => setWarning(null), 4000);
        }
        sendMessage(user.id, convId, txt);
        setDraft("");
    };

    const online = isPeerOnline(conv.peerId);

    // Peer's business services — used to show "Book Service" in chat header.
    const canBook = peerServices.length > 0 && peerUser?.accountType === "business";

    const handleBookService = () => {
        if (!peerServices.length) return;
        const target = peerServices[0];
        // Pre-fill a booking intent message so the chat reflects the action.
        const summary = `📅 Booking requested — ${target.name}`;
        try { sendMessage(user.id, convId, summary); } catch {}
        navigate(`/services/${target.id}`);
    };

    return (
        <div className="screen" data-testid="chat-screen">
            <header className="px-3 pt-[max(12px,env(safe-area-inset-top))] pb-2 border-b border-border/60">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="chat-back-btn"
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95"
                        aria-label="Back"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <button
                        onClick={() => navigate(`/u/${conv.peerUsername}`)}
                        data-testid="chat-peer-btn"
                        className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 active:scale-[0.99] transition-[opacity,transform]"
                    >
                        <div className="relative">
                            <InitialAvatar name={conv.peerName} size={36} />
                            {online && (
                                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                            )}
                        </div>
                        <div className="min-w-0 text-left">
                            <div className="font-display text-[14.5px] font-semibold truncate inline-flex items-center gap-1.5">
                                {conv.peerName}
                                {muted && <BellOff size={12} className="text-muted-foreground" data-testid="chat-muted-icon" />}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">
                                {online ? "Online" : `@${conv.peerUsername}`}
                            </div>
                        </div>
                    </button>
                    <button
                        data-testid="chat-mute-btn"
                        onClick={() => toggleMute(user.id, "direct", convId)}
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95 text-muted-foreground"
                        aria-label={muted ? "Unmute" : "Mute"}
                        aria-pressed={muted}
                        title={muted ? "Unmute" : "Mute"}
                    >
                        {muted ? <BellOff size={18} /> : <Bell size={18} />}
                    </button>
                </div>
                {canBook && (
                    <button
                        data-testid="chat-book-service-btn"
                        onClick={handleBookService}
                        className="mt-2 w-full h-11 rounded-full text-primary-foreground inline-flex items-center justify-center gap-2 text-[13px] font-semibold active:scale-[0.98] transition-transform"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        <Briefcase size={14} />
                        Book {peerServices[0].name}
                    </button>
                )}
            </header>

            {warning && (
                <div
                    data-testid="chat-warning"
                    role="alert"
                    className="mx-3 mt-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[12px] inline-flex items-center gap-2"
                >
                    <ShieldAlert size={13} className="shrink-0" />
                    <span>{warning}</span>
                </div>
            )}

            <div
                ref={scrollerRef}
                className="flex-1 overflow-y-auto no-scrollbar px-4 pt-4 pb-2 space-y-2"
                data-testid="chat-messages"
            >
                {messages.map((m, idx) => {
                    const mine = m.from === "me";
                    const showTime =
                        idx === 0 || m.at - messages[idx - 1].at > 5 * 60_000;
                    const unsafe = detectUnsafeContent(m.text);
                    const shouldBlur = unsafe && violations >= VIOLATION_BLUR_THRESHOLD;
                    return (
                        <div key={m.id}>
                            {showTime && (
                                <div className="text-center text-[10.5px] text-muted-foreground my-2">
                                    {timeOfDay(m.at)}
                                </div>
                            )}
                            <div className={["flex", mine ? "justify-end" : "justify-start"].join(" ")}>
                                <div
                                    data-testid={mine ? "chat-bubble-me" : "chat-bubble-them"}
                                    className={[
                                        "max-w-[78%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-snug",
                                        mine
                                            ? "bg-primary text-primary-foreground rounded-br-md shadow-soft"
                                            : "bg-secondary text-secondary-foreground rounded-bl-md",
                                    ].join(" ")}
                                >
                                    {shouldBlur ? (
                                        <BlurredMessage testId={mine ? "chat-blur-me" : "chat-blur-them"} text={m.text} />
                                    ) : m.text}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className="h-2" />
            </div>

            <div className="px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] bg-background border-t border-border/60">
                {isMessagingSuspended(user) && (
                    <p
                        data-testid="chat-messaging-suspended-banner"
                        className="mb-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[12px] font-medium text-center"
                    >
                        Messaging is suspended on your account. Contact support for help.
                    </p>
                )}
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center h-12 px-4 rounded-full bg-secondary focus-within:bg-background focus-within:ring-2 focus-within:ring-primary/40 transition-[background-color,box-shadow]">
                        <input
                            data-testid="chat-input"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && onSend()}
                            placeholder="Message…"
                            className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/80"
                        />
                    </div>
                    <button
                        data-testid="chat-send-btn"
                        onClick={onSend}
                        disabled={!draft.trim()}
                        className="h-12 w-12 flex items-center justify-center rounded-full text-primary-foreground shadow-card disabled:opacity-40 active:scale-95 transition-[transform,opacity]"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        }}
                        aria-label="Send"
                    >
                        <Send size={18} strokeWidth={2.4} />
                    </button>
                </div>
            </div>
        </div>
    );
};

const BlurredMessage = ({ testId, text }) => {
    const [revealed, setRevealed] = useState(false);
    return (
        <div className="inline-flex flex-col items-start gap-1" data-testid={testId}>
            <span
                className={revealed ? "" : "blur-sm select-none"}
                style={revealed ? undefined : { filter: "blur(6px)" }}
            >
                {text}
            </span>
            <button
                onClick={() => setRevealed((r) => !r)}
                className="inline-flex items-center gap-1 text-[10.5px] font-medium underline opacity-90"
            >
                <EyeOff size={10} />
                {revealed ? "Hide" : "Show anyway"}
            </button>
        </div>
    );
};

export default Chat;
